import type { Documentation, OldReport } from '@si/types';
import { del } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface DeleteParams {
  projectId: string;
  documentationId: string | number;
}

export function useDeleteReport() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation<void, Error, DeleteParams>({
    mutationFn: ({ projectId, documentationId }: DeleteParams) =>
      del({
        url: `/projects/${projectId}/reports/${documentationId}`,
      }),
    onSuccess: (_, { projectId, documentationId }) => {
      const getReportsQueryKey = queryKeys.getReports(projectId);
      const cachedReports = queryClient.getQueryData<{
        reports: Documentation[];
        oldReports: OldReport[];
      }>(getReportsQueryKey);

      if (cachedReports) {
        queryClient.setQueryData(getReportsQueryKey, {
          reports: cachedReports.reports.filter(
            ({ documentationId: id }) => id !== documentationId
          ),
          oldReports: cachedReports.oldReports,
        });
      }
    },
  });

  const deleteReport = useCallback((params: DeleteParams) => mutate(params), [mutate]);

  return {
    deleteReport,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
